/**
 * Settings modal hook for handling scroll-to functionality
 *
 * Handles scrolling to specific sections within the settings modal
 * when opened from different contexts (e.g., from engine selector).
 * Provider links in the API keys tab open through Wavebox.
 */
import { openInWavebox } from '../lib/wavebox_link_opener.js'

export default {
  mounted () {
    // Find the parent dialog element
    this.dialog = this.el.closest('dialog')

    // Store bound handlers for proper cleanup
    this.boundFormKeydown = this.handleFormKeydown.bind(this)

    // Track current input elements for cleanup
    this.currentForm = null

    // Listen for the scroll-to-api-keys event
    this.handleEvent('scroll-to-api-keys', () => {
      // Wait for dialog to be fully open before scrolling
      if (this.dialog && !this.dialog.open) {
        // If dialog not open yet, wait for it
        this.dialog.addEventListener('transitionend', () => {
          this.scrollToApiKeys()
        }, { once: true })
      } else {
        this.scrollToApiKeys()
      }
    })

    // Links to provider sites open through Wavebox so they land in the
    // user's Space rather than a stray window.
    this.boundExternalClick = this.handleExternalClick.bind(this)
    this.el.addEventListener('click', this.boundExternalClick)

    // Setup Enter key handling for skill editor form
    this.setupSkillEditorKeyHandling()
  },

  scrollToApiKeys () {
    const apiKeysSection = document.getElementById('api-keys-section')
    if (apiKeysSection) {
      apiKeysSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  },

  updated () {
    // Re-setup handlers in case inputs were re-rendered
    this.setupSkillEditorKeyHandling()
  },

  handleExternalClick (e) {
    const link = e.target.closest && e.target.closest('a[data-open-in-wavebox]')
    if (!link) {return}
    e.preventDefault()
    openInWavebox(link.href, { elevate: true })
  },

  handleFormKeydown (e) {
    // Only intercept Enter key
    if (e.key !== 'Enter') {return}

    // Allow Enter in textareas (for multi-line input)
    if (e.target.tagName === 'TEXTAREA') {return}

    // For single-line inputs, prevent form submission
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') {
      e.preventDefault()

      // Move to next focusable element
      const form = e.currentTarget
      const focusableElements = Array.from(form.querySelectorAll(
        'input:not([type="hidden"]), select, textarea, button[type="submit"]'
      ))
      const currentIndex = focusableElements.indexOf(e.target)
      if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
        focusableElements[currentIndex + 1].focus()
      }
    }
  },

  setupSkillEditorKeyHandling () {
    const form = document.getElementById('skill-editor-form')

    // Clean up old handler if form changed
    if (this.currentForm && this.currentForm !== form) {
      this.currentForm.removeEventListener('keydown', this.boundFormKeydown)
    }

    // Set up new handler if form exists and is different
    if (form && form !== this.currentForm) {
      form.addEventListener('keydown', this.boundFormKeydown)
      this.currentForm = form
    }
  },

  destroyed () {
    // Clean up all event listeners
    this.el.removeEventListener('click', this.boundExternalClick)
    if (this.currentForm) {
      this.currentForm.removeEventListener('keydown', this.boundFormKeydown)
    }
  }
}
